<script lang="ts">
    import ColorThumbs from '@tile/components/__experiments__/e3801b/color-thumbs/ColorThumbs.svelte';
    import Delivery from '@tile/components/__experiments__/e3801b/delivery/Delivery.svelte';
    import ShortTitle from '@tile/components/__experiments__/e3801b/product-title/ShortTitle.svelte';
    import SustainabilityBadge from '@tile/components/__experiments__/e3801b/sustainability-badge/SustainabilityBadge.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import ProductComparisonCheckBox from '@tile/components/product-comparison/ProductComparisonCheckBox.svelte';
    import ProductImageCarousel from '@tile/components/product-image-carousel/ProductImageCarousel.svelte';
    import ProductImage from '@tile/components/product-image/ProductImage.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import ProductLink from '@tile/components/product-title/ProductLink.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import Rating from '@tile/components/rating/Rating.svelte';
    import SponsoredHint from '@tile/components/sponsored-hint/SponsoredHint.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { AdditionalImagesType, ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import PriceDispatcher from '@tilelist/layouts/experiments/price-dispatcher/PriceDispatcher.svelte';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';
    import type { DefaultLayout } from './DefaultLayout.ts';

    type Props = TileLayoutProps & {
        layout: DefaultLayout;
        aspectRatio: ImageAspectRatio;
        showInstallments: boolean;
        bestVariationBenefit?: string;
    };

    let {
        advertiserLegalName,
        aspectRatio,
        colors,
        element = $bindable(null),
        funderLegalName,
        imageLoadingOptions,
        onRedirectToPdp,
        rule,
        showInstallments,
        sponsored,
        layout,
        bestVariationBenefit,
    }: Props = $props();

    const wiring = injectWiring(['trackingService']);
    const onex = wiring.onex;

    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const { variation, title, link: rawLink } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);

    const hasMultipleColors = $derived((colors?.length ?? 0) > 1);
    const hasSustainabilityBadge = $derived((variation.sustainabilityBadges?.length ?? 0) > 0);
    const showColorBadgeRow = $derived(hasMultipleColors || hasSustainabilityBadge);

    const additionalImagesMapping: Record<ExperimentGroups['ADDITIONAL_IMAGES'], AdditionalImagesType> = {
        E3799A: 'Single',
        E3799B: 'CarouselV1',
        E3799C: 'CarouselV2',
    };

    const additionalImagesGroup = $derived(onex.matchGroup('ADDITIONAL_IMAGES', additionalImagesMapping));
</script>

<ProductComparisonCheckBox currentVariation={variation} />

<div data-jarvis="reptile-tile-container" data-variation-id={variation.variationId} bind:this={element}>
    {#if additionalImagesGroup === 'CarouselV1' || additionalImagesGroup === 'CarouselV2'}
        <ProductImageCarousel
            images={layout.images}
            fallbackImage={layout.primaryImage}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            brand={variation.brand || ''}
            title={variation.title?.withoutBrand || variation.title?.full || ''}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            onImageChange={() => {
                wiring.trackingService?.trackInteraction('product_image_change');
            }}
            deal={variation.deal}
            {aspectRatio}
            socialProof={variation.socialProof}
            {rule}
            size={additionalImagesGroup === 'CarouselV1' ? '100' : '50'}
            hidePaginationDots={additionalImagesGroup === 'CarouselV1'}
        />
    {:else}
        <ProductImage
            variationId={variation.variationId}
            image={layout.primaryImage}
            socialProof={variation.socialProof}
            detailPageLink={variation.detailPageLink}
            onClick={onRedirectToPdp}
            deal={variation.deal}
            {imageLoadingOptions}
            {aspectRatio}
            {rule}
        />
    {/if}
    <div class="reptile-tile__content">
        <header class="reptile-tile__header">
            <SponsoredHint
                sponsored={sponsored ?? false}
                funderLegalName={funderLegalName ?? ''}
                advertiserLegalName={advertiserLegalName ?? ''}
            />
            <ProductLink {link} onClick={onRedirectToPdp}>
                <ShortTitle {title} brandOnTop={true} />
            </ProductLink>

            <TopInfo infos={variation.topInfos} />
        </header>
        <EekLabel labels={variation?.energyLabels || []} />
        <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
        <Rating customerReviews={variation.customerReviews} />
        <PriceDispatcher
            variationId={variation.variationId}
            saleTags={variation?.saleTags}
            price={variation.price}
            dealHighlight={variation.deal?.highlight}
            {showInstallments}
            {rule}
            {bestVariationBenefit}
        />
        <Delivery availability={variation.availability} ottoUp={variation.ottoUp} />
        {#if showColorBadgeRow}
            <div class="reptile-tile__color-badge-row">
                <ColorThumbs
                    {colors}
                    currentVariationId={variation.variationId}
                    detailPageLink={variation.detailPageLink}
                    productLinkTitle={variation.title?.full || ''}
                    onClick={onRedirectToPdp}
                />
                <SustainabilityBadge badges={variation.sustainabilityBadges} />
            </div>
        {/if}
    </div>
    <JsonLd {variation} />
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tile {
        &__content,
        &__header {
            display: flex;
            flex-direction: column;
        }

        &__content {
            padding: tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-75 tokens.$oc-semantic-spacing-75
                tokens.$oc-semantic-spacing-75;
            row-gap: tokens.$oc-semantic-spacing-75;

            @include mixins.breakpoint-from-l() {
                padding: tokens.$oc-semantic-spacing-75;
            }
        }

        &__header {
            row-gap: tokens.$oc-semantic-spacing-default-between-blocks;
        }

        &__color-badge-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            column-gap: tokens.$oc-semantic-spacing-50;
            row-gap: tokens.$oc-semantic-spacing-75;

            /*                                                            */
            /*                                              */
            :global(.reptile-color-thumbs) {
                min-width: 0;
            }
        }
    }
</style>
